import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Layout from './components/Layout';
import AppShellLayout from './components/layout/AppShellLayout';
import DefaultLanding from './components/routing/DefaultLanding';
import ConsumerUnderstanding from './pages/ConsumerUnderstanding';
import TCFFramework from './pages/TCFFramework';
import VerbatimsList from './pages/VerbatimsList';
import DataImport from './pages/DataImport';
import ReportExport from './pages/ReportExport';
import UniversalAuthPage from './pages/UniversalAuthPage';
import OAuthDeviceApprovalPage from './pages/OAuthDeviceApprovalPage';
import InsightDetailPage from './pages/InsightDetailPage';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import OmbudsmanRoute from './components/OmbudsmanRoute';
import RootRedirect from './components/routing/RootRedirect';
import Manifesto from './components/Manifesto';
import { FrameworkProvider } from './contexts/FrameworkContext';
import { CustomerProvider } from './contexts/CustomerContext';
import { AuthProvider } from './contexts/AuthContext';
import { CaseProvider } from './contexts/CaseContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { RealtimeProvider } from './contexts/RealtimeProvider';
import UnifiedCaseRegistry from './pages/UnifiedCaseRegistry';
import ConsumerChampion from './components/ConsumerChampion';
import RadarView from './components/RadarView';
import UsersManagement from './pages/UsersManagement';
import V4OrganizationUsers from './pages/V4OrganizationUsers';
import OrganizationsManagement from './pages/OrganizationsManagement';
import AdminTaskMonitorPage from './pages/AdminTaskMonitorPage';
import UserProfile from './pages/UserProfile';
import TaxonomyVisualization from './pages/TaxonomyVisualization';
import TaxonomyExpandDebug from './pages/TaxonomyExpandDebug';
import OmbudsmanManagement from './pages/OmbudsmanManagement';

// V3 imports
import V3Layout from './components/v3/layout/V3Layout';
import V3SettingsLayout from './components/v3/settings/V3SettingsLayout';
import V3SettingsIndex from './components/v3/settings/V3SettingsIndex';
import OmbudsmanDecisionsView from './components/v3/settings/OmbudsmanDecisionsView';
import V3UsersManagement from './components/v3/settings/V3UsersManagement';
import TeamsView from './components/v3/settings/TeamsView';
import FeatureFlagsView from './components/v3/settings/FeatureFlagsView';
import AuditTrailView from './components/v3/settings/AuditTrailView';
import RolesView from './components/v3/settings/RolesView';
import UsersManagementView from './components/v3/settings/UsersManagementView';
import OrganizationDetailsView from './components/v3/settings/OrganizationDetailsView';
import TaxonomySettings from './components/v3/settings/TaxonomySettings';
import ConductIntelligenceFeed from './components/v3/consumer-duty/ConductIntelligenceFeed';
import SignalsLiveDetail from './components/v3/signals-live/SignalsLiveDetail';
import SignalDetailPage from './components/v3/consumer-duty/feed/SignalDetailPage';
import APIKeysView from './components/v3/settings/APIKeysView';
import BillingProviderKeysView from './components/v3/settings/BillingProviderKeysView';
import BillingUsageView from './components/v3/settings/BillingUsageView';
import ConnectorsView from './components/v3/settings/ConnectorsView';
import SkillsView from './components/v3/settings/SkillsView';
import TodayDashboardV4 from './components/v3/today/TodayDashboardV4';
import JunoBriefing from './components/v3/briefing/JunoBriefing';
import FOSAdminDashboard from './components/v3/admin/FOSAdminDashboard';
import FeaturesVisibilitySettingsPage from './components/v3/admin/FeaturesVisibilitySettingsPage';
import DisplaySettingsPage from './components/v3/admin/DisplaySettingsPage';
import TaskQueueSettingsPage from './components/v3/admin/TaskQueueSettingsPage';
import AdminImportsPage from './components/v3/admin/AdminImportsPage';
import AdminImportDetailPage from './components/v3/admin/AdminImportDetailPage';
import ScheduledJobsPage from './components/v3/admin/scheduled-jobs/ScheduledJobsPage';
import ImportJobsAdminPage from './components/v3/admin/import-jobs/ImportJobsAdminPage';
import ProfilePage from './pages/v3/ProfilePage';
import { NoticeProvider } from './components/v3/shared/NoticeProvider';
import RealtimeExamplesDashboard from './components/examples/RealtimeExamples';
import OmbudsmanWorkspaceHub from './components/v3/workspace/ombudsman/OmbudsmanWorkspaceHub';
import CaseWorkspace from './components/v3/workspace/ombudsman/CaseWorkspace';
import OmbudsmanWorkspace from './components/v3/ombudsman/OmbudsmanWorkspace';

// V4 imports - Experimental modular records
import RecordsPage from './components/v4/records/RecordsPage';
import CollectionsPage from './components/v4/records/CollectionsPage';
import CollectionOverviewPage from './components/v4/records/CollectionOverviewPage';
import RecordDetailPage from './components/v4/records/RecordDetailPage';
import MonitorSetupPage from './components/v4/monitor/MonitorSetupPage';
import TodayPage from './components/v4/today/TodayPage';
import EvaluationPage from './components/v4/evaluation/EvaluationPage';
import TaxonomyPage from './components/v4/golab/taxonomy/TaxonomyPage';
import PromptsPage from './components/v4/golab/prompts/PromptsPage';
import GoLabLayout from './components/v4/golab/GoLabLayout';
import GoLabHomePage from './components/v4/golab/HomePage';
import DatasetsHomePage from './components/v4/golab/datasets/DatasetsHomePage';
import CollectionDetailPage from './components/v4/golab/datasets/CollectionDetailPage';
import AnnotateLayout from './components/v4/golab/annotate/AnnotateLayout';
import AnnotateQueuePage from './components/v4/golab/annotate/AnnotateQueuePage';
import ReviewQueuePage from './components/v4/golab/annotate/ReviewQueuePage';
import AnnotationFlowPage from './components/v4/golab/annotate/AnnotationFlowPage';
import AdjudicationFlowPage from './components/v4/golab/annotate/AdjudicationFlowPage';
import ReportBuilderPage from './components/v4/reports/ReportBuilderPage';
import { CauseReportPage } from './components/v4/reports/cause-report/CauseReportPage';
import { OutcomesMonitoringReportPage } from './components/v4/reports/outcomes-monitoring';

import './App.css';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (was cacheTime in v4)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Legacy /v3/auth alias → /auth, PRESERVING the query string. Magic-link /
// invitation / password-setup links are emitted as /v3/auth?token=…; a plain
// <Navigate to="/auth"> drops the token, so the token page opens empty and the
// user can't verify or set a password. Keep location.search on the redirect.
function LegacyAuthRedirect() {
  const location = useLocation();
  return <Navigate to={`/auth${location.search}`} replace />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <NoticeProvider position="bottom-center">
        <AuthProvider>
          <RealtimeProvider>
          <WebSocketProvider>
          <FrameworkProvider>
              <CustomerProvider>
                <CaseProvider>
              <Routes>
                <Route path="/" element={<RootRedirect />} />
                <Route path="/manifesto" element={<Manifesto />} />
                <Route path="/auth" element={<UniversalAuthPage />} />
                <Route path="/oauth/device" element={<OAuthDeviceApprovalPage />} />
                <Route path="/login" element={<Navigate to="/auth" replace />} />
                <Route path="/v3/auth" element={<LegacyAuthRedirect />} />

                {/* Legacy auth aliases */}
                <Route path="/auth/login" element={<Navigate to="/auth" replace />} />
                <Route path="/auth/signup" element={<Navigate to="/auth" replace />} />

                {/* Legacy admin aliases (pre-v3 URLs) */}
                <Route
                  path="/admin/scheduled-jobs"
                  element={<Navigate to="/v3/settings/scheduled-jobs" replace />}
                />

                {/* Protected Routes */}
                <Route element={<ProtectedRoute />}>
                  {/* Case Guidance checkpoint routes on the new app shell */}
                  <Route path="/v3/ombudsman" element={<AppShellLayout />}>
                    {/* Sep-3: cases/FOS tooling is internal — platform only. */}
                    <Route element={<AdminRoute require="platform" />}>
                      <Route index element={<OmbudsmanWorkspace />} />
                      <Route path="simulations/:simulationId" element={<CaseWorkspace />} />
                    </Route>
                  </Route>

                  <Route path="/v3/workspace/ombudsman" element={<AppShellLayout />}>
                    <Route element={<AdminRoute require="platform" />}>
                      <Route index element={<OmbudsmanWorkspaceHub />} />
                      <Route path=":simulationId" element={<CaseWorkspace />} />
                    </Route>
                  </Route>

                  <Route path="/dashboard" element={<Layout />}>
                    <Route index element={<Navigate to="/v4/records" replace />} />
                    <Route path="complaints" element={<Navigate to="/v4/records" replace />} />
                    <Route path="complaints-v2" element={<Navigate to="/v4/records" replace />} />
                    <Route path="today" element={<Navigate to="/v4/today" replace />} />
                    <Route path="radar" element={<RadarView />} />
                    <Route path="complaints-v3" element={<Navigate to="/v4/records" replace />} />
                    <Route path="live-registry" element={<Navigate to="/v4/records" replace />} />
                    <Route path="unified-case-registry" element={<UnifiedCaseRegistry />} />
                    <Route path="consumer-champion" element={<ConsumerChampion />} />
                    <Route path="insights/:id" element={<InsightDetailPage />} />
                    <Route path="consumer-understanding" element={<ConsumerUnderstanding />} />
                    <Route path="tcf-framework" element={<TCFFramework />} />
                    <Route path="verbatims" element={<VerbatimsList />} />
                    <Route path="import" element={<DataImport />} />
                    <Route path="reports" element={<ReportExport />} />
                    <Route path="profile" element={<UserProfile />} />
                    
                    {/* clusters & root-causes routes removed */}
                    
                    {/* Internal platform admin pages (Sep-3): platform only. */}
                    <Route element={<AdminRoute require="platform" />}>
                      <Route path="admin/users" element={<UsersManagement />} />
                      <Route path="admin/organizations" element={<OrganizationsManagement />} />
                      <Route path="admin/tasks" element={<AdminTaskMonitorPage />} />
                      <Route path="admin/ombudsman" element={<OmbudsmanManagement />} />
                    </Route>
                  </Route>

                  <Route path="/v3/settings" element={<AppShellLayout />}>
                    <Route element={<V3SettingsLayout />}>
                      <Route index element={<V3SettingsIndex />} />

                      {/* Organization details — every signed-in org user may see
                          which organization they belong to (Sep-3). */}
                      <Route path="organization" element={<OrganizationDetailsView />} />

                      {/* Organization administration — org admins (firm.manage;
                          audit rides along). The ONLY settings an organization
                          sees besides Details: Users & Permissions, Teams,
                          Audit Trail (Sep-3). */}
                      <Route element={<AdminRoute require="firm" />}>
                        <Route path="users" element={<V3UsersManagement />} />
                        <Route path="rbac-users" element={<UsersManagementView />} />
                        <Route path="teams" element={<TeamsView />} />
                        <Route path="roles" element={<RolesView />} />
                      </Route>

                      {/* Audit trail — read-only audit access (firm.audit_view)
                          is enough, matching the nav gate and the backend's
                          FIRM_AUDIT_VIEW check. The plain 'firm' scope requires
                          firm.manage and would bounce an audit-only principal. */}
                      <Route element={<AdminRoute require="firm_audit" />}>
                        <Route path="audit-trail" element={<AuditTrailView />} />
                      </Route>

                      {/* Internal platform surfaces (Sep-3): platform
                          administrators only — matching the backend gates, so
                          neither the nav, the URL, nor the endpoints reach an
                          organization account. */}
                      <Route element={<AdminRoute require="platform" />}>
                        <Route path="connectors" element={<ConnectorsView />} />
                        <Route path="api-keys" element={<APIKeysView />} />
                        <Route path="skills" element={<SkillsView />} />
                        <Route path="notifications" element={<div className="p-6">Notifications - Coming Soon</div>} />
                        <Route path="rbac-features" element={<FeatureFlagsView />} />
                        <Route path="billing/provider-keys" element={<BillingProviderKeysView />} />
                        <Route path="billing/usage" element={<BillingUsageView />} />
                        <Route path="ombudsman" element={<OmbudsmanDecisionsView />} />
                        <Route path="fos" element={<FOSAdminDashboard />} />
                        <Route path="features-visibility" element={<FeaturesVisibilitySettingsPage />} />
                        <Route path="display-settings" element={<DisplaySettingsPage />} />
                        <Route path="task-queue" element={<TaskQueueSettingsPage />} />
                        <Route path="imports" element={<AdminImportsPage />} />
                        <Route path="taxonomy" element={<TaxonomySettings />} />
                        <Route path="scheduled-jobs" element={<ScheduledJobsPage />} />
                        <Route path="import-jobs" element={<ImportJobsAdminPage />} />
                      </Route>
                    </Route>
                  </Route>

                  <Route path="/v3/taxonomy" element={<AppShellLayout />}>
                    <Route element={<AdminRoute require="platform" />}>
                      <Route index element={<TaxonomyVisualization />} />
                    </Route>
                  </Route>

                  <Route path="/v3/taxonomy-expand-debug" element={<AppShellLayout />}>
                    <Route element={<AdminRoute require="platform" />}>
                      <Route index element={<TaxonomyExpandDebug />} />
                    </Route>
                  </Route>

                  {/* V3 Routes - New Good Outcomes UI with Olivia Intelligence */}
                  <Route path="/v3" element={<V3Layout />}>
                    {/* Landing page for all users: Conduct Intelligence Signals Feed */}
                    <Route index element={<ConductIntelligenceFeed />} />

                    {/* Signals Live routes - Real backend integration */}
                    <Route path="signals-live" element={<ConductIntelligenceFeed />} />
                    <Route path="signals-live/:signalId" element={<SignalsLiveDetail />} />
                    <Route path="signals-old" element={<Navigate to="/v4/records" replace />} />
                    
                    {/* Standalone signal detail page (pop-out from list) */}
                    <Route path="signals/:signalId" element={<SignalDetailPage />} />

                    {/* Conduct Vault route - DEPRECATED: Redirect to V4 */}
                    <Route path="vault" element={<Navigate to="/v4/records" replace />} />

                    {/* Removed deprecated signals mockup routes */}
                    
                    {/* Today Dashboard - Redirect to new V4 Today Page */}
                    <Route path="today" element={<Navigate to="/v4/today" replace />} />

                    {/*
                      Legacy Today Dashboard (preserved for rollback)

                      ROLLBACK INSTRUCTIONS:
                      If V4 Today Page has issues, you can temporarily rollback by:
                      1. Change the redirect above from "/v4/today" to "/v3/today-legacy"
                      2. Update UniversalAuthPage.tsx post-login redirect
                      3. Update OMBUDSMAN_REDIRECT_PATH in utils/rbac.ts

                      This route can be removed once V4 Today Page is stable.
                    */}
                    <Route path="today-legacy" element={<TodayDashboardV4 />} />
                    
                    {/* Morning Briefing */}
                    <Route path="briefing" element={<JunoBriefing />} />
                    <Route path="cases" element={<div className="p-6">Cases - Coming Soon</div>} />

                    <Route path="reporting" element={<div className="p-6">Reporting - Coming Soon</div>} />
                    <Route path="profile" element={<ProfilePage />} />
                    
                    {/* Admin-only import detail page */}
                    <Route element={<AdminRoute />}>
                      <Route element={<AdminRoute require="platform" />}>
                        <Route path="admin/imports/:sessionId" element={<AdminImportDetailPage />} />
                      </Route>
                    </Route>

                    {/* Admin-only: Real-time System Examples */}
                    <Route element={<AdminRoute />}>
                      <Route element={<AdminRoute require="platform" />}>
                        <Route path="examples/realtime" element={<RealtimeExamplesDashboard />} />
                      </Route>
                    </Route>
                  </Route>

                  {/* V4 Routes - Experimental modular records prototype */}
                  <Route path="/v4" element={<AppShellLayout />}>
                    {/* Capability-aware start page (platform admin -> GO Lab,
                        monitoring users -> Monitor conduct, etc.) */}
                    <Route index element={<DefaultLanding />} />
                    {/* Today Page - Single-Screen Intelligence Briefing */}
                    <Route path="today" element={<TodayPage />} />
                    {/* Collections list */}
                    <Route path="records" element={<CollectionsPage />} />
                    {/* Collection Overview — gateway between home and records */}
                    <Route path="records/:collectionId" element={<CollectionOverviewPage />} />
                    {/* Records in collection */}
                    <Route path="records/:collectionId/records" element={<RecordsPage />} />
                    {/* Single record detail */}
                    <Route path="records/:collectionId/:recordId" element={<RecordDetailPage />} />
                    {/* Classification evaluation is internal (Sep-3): platform only. */}
                    <Route element={<AdminRoute require="platform" />}>
                      <Route path="evaluate" element={<EvaluationPage />} />
                    </Route>
                    {/* Monitor setup wizard — org-admin work (add a source, pull
                        data). Gated on firm administration so an RBAC org admin
                        (FIRM_MANAGE), not only a legacy role="admin", reaches it. */}
                    <Route element={<AdminRoute require="firm" />}>
                      <Route path="monitor/new" element={<MonitorSetupPage />} />
                    </Route>
                    {/* GO Lab — reachable with a GO Lab capability (PLATFORM_ADMIN
                        or a LAB_* role), not only legacy admin. */}
                    <Route element={<AdminRoute require="golab" />}>
                      <Route path="go-lab" element={<GoLabLayout />}>
                        <Route index element={<GoLabHomePage />} />

                        <Route
                          path="intelligence"
                          element={<Navigate to="/v4/go-lab/intelligence/taxonomy" replace />}
                        />
                        <Route path="intelligence/taxonomy" element={<TaxonomyPage />} />
                        <Route path="intelligence/taxonomy/:taxonomyKey" element={<TaxonomyPage />} />
                        <Route path="intelligence/prompts" element={<PromptsPage />} />
                        <Route path="intelligence/prompts/:promptKey" element={<PromptsPage />} />

                        <Route path="datasets" element={<DatasetsHomePage />} />
                        <Route path="datasets/:collectionId">
                          <Route index element={<Navigate to="datasets" replace />} />
                          <Route path=":tab" element={<CollectionDetailPage />} />
                        </Route>

                        <Route path="annotate" element={<AnnotateLayout />}>
                          <Route index element={<Navigate to="/v4/go-lab/annotate/queue" replace />} />
                          <Route path="queue" element={<AnnotateQueuePage />} />
                          <Route path="review" element={<ReviewQueuePage />} />
                        </Route>
                      </Route>
                      {/* Full-screen annotation flow — sibling of go-lab parent
                          so it renders outside GoLabLayout / AnnotateLayout chrome.
                          react-router picks this more-specific match over the
                          nested annotate/queue route for paths with :recordId. */}
                      <Route path="go-lab/annotate/queue/:recordId" element={<AnnotationFlowPage />} />
                      <Route path="go-lab/annotate/review/:recordId" element={<AdjudicationFlowPage />} />
                    </Route>
                    {/* Platform administration — org management + onboarding.
                        Reached from the "New organization" button in the
                        cross-org bar. platform.manage (PLATFORM_ADMIN) only. */}
                    <Route element={<AdminRoute require="platform" />}>
                      <Route path="admin/organizations" element={<OrganizationsManagement />} />
                      <Route path="admin/users" element={<V4OrganizationUsers />} />
                    </Route>
                  </Route>

                  <Route path="/v4/reports" element={<AppShellLayout />}>
                    {/* Analyst Canvas (the index / report builder) is an internal
                        surface — platform admins only (Sep-10), guarding direct URL
                        access to match the nav gate. The collection reports
                        (cause/outcome/tcf) stay OUTSIDE this guard: monitoring
                        users reach them from the report tiles on a collection and
                        the report APIs authorize them via monitoring.view. */}
                    <Route element={<AdminRoute require="platform" />}>
                      <Route index element={<ReportBuilderPage />} />
                    </Route>
                    <Route path="cause/:datasetId" element={<CauseReportPage />} />
                    <Route path="outcome/:datasetId" element={<OutcomesMonitoringReportPage framework="cd" />} />
                    <Route path="tcf/:datasetId" element={<OutcomesMonitoringReportPage framework="tcf" />} />
                  </Route>
                </Route>
              </Routes>
              </CaseProvider>
            </CustomerProvider>
          </FrameworkProvider>
        </WebSocketProvider>
        </RealtimeProvider>
      </AuthProvider>
      </NoticeProvider>
    </Router>
    </QueryClientProvider>
  );
}

export default App;
